// LoadMatcher.tsx — AI driver ↔ load matching panel
// Shows in Command Center. Scores every available driver against every open load.

import React, { useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

interface Props {
  companyId: string | null;
  onAssign?: (driverId: string, loadId: string) => void;
}

const band = (b: string) => ({ GREEN:'#32CD32', YELLOW:'#FFD700', RED:'#ff4444' }[b] || '#555');
const scoreColor = (s: number) => s >= 75 ? '#32CD32' : s >= 50 ? '#FFD700' : s >= 25 ? '#FF8C00' : '#ff4444';

export default function LoadMatcher({ companyId, onAssign }: Props) {
  const [matches, setMatches] = useState<any[]>([]);
  const [allMatches, setAllMatches] = useState<any[]>([]);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState<'top' | 'matrix'>('top');
  const [assigning, setAssigning] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Record<string, boolean>>({});
  const [meta, setMeta] = useState<any>(null);

  const runMatcher = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(''); setSummary(''); setMatches([]); setAllMatches([]);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('load-matcher', {
        body: { companyId, mode: 'auto' },
      });
      if (fnErr) throw new Error(fnErr.message);
      if (data?.error) throw new Error(data.error);
      setMatches(data.top_matches || []);
      setAllMatches(data.all_matches || []);
      setSummary(data.summary || '');
      setMeta({ drivers: data.drivers_count, loads: data.loads_count, busy: data.busy_count });
    } catch (e: any) {
      setError(e.message);
    }
    setLoading(false);
  }, [companyId]);

  const handleAssign = async (driverId: string, loadId: string, driverName: string, loadNumber: string) => {
    const key = `${driverId}-${loadId}`;
    setAssigning(key);
    const { error: upErr } = await supabase.from('loads').update({
      assigned_driver_id: driverId,
      workflow_status: 'DISPATCHED',
      status: 'ACTIVE',
      dispatched_at: new Date().toISOString(),
    }).eq('id', loadId);
    if (!upErr) {
      setAssigned(prev => ({ ...prev, [key]: true }));
      // Fire rate conf email + push notify
      supabase.functions.invoke('rate-conf-email', { body: { loadId, companyId } }).catch(() => {});
      supabase.functions.invoke('push-notify', { body: { driverId, title: 'Load Assigned', body: `Load #${loadNumber} dispatched to you. Check your load details.`, url: '/driver' } }).catch(() => {});
      if (onAssign) onAssign(driverId, loadId);
    }
    setAssigning(null);
  };

  const S = {
    panel: { background:'#050505', border:'1px solid #1e1e1e', padding:'20px' } as React.CSSProperties,
    header: { color:'#32CD32', fontSize:'11px', letterSpacing:'2px', fontWeight:700, marginBottom:'4px' } as React.CSSProperties,
    sub: { color:'#555', fontSize:'11px', marginBottom:'16px' } as React.CSSProperties,
    btn: (active?: boolean) => ({ background: active ? '#32CD32' : 'transparent', color: active ? '#000' : '#32CD32', border:'1px solid #32CD32', padding:'8px 16px', fontFamily:'monospace', fontSize:'10px', fontWeight:700, letterSpacing:'1px', cursor:'pointer' } as React.CSSProperties),
    card: { background:'#000', border:'1px solid #1a1a1a', padding:'14px', marginBottom:'10px' } as React.CSSProperties,
    score: (s: number) => ({ display:'inline-block', padding:'3px 8px', background:scoreColor(s), color:'#000', fontWeight:700, fontSize:'11px', fontFamily:'monospace', minWidth:'36px', textAlign:'center' as const }),
    tag: { background:'#0a0a0a', border:'1px solid #1a1a1a', color:'#555', padding:'2px 6px', fontSize:'9px', fontFamily:'monospace', marginRight:'4px', marginBottom:'2px', display:'inline-block' } as React.CSSProperties,
    assignBtn: (done: boolean) => ({ background: done ? 'transparent' : '#32CD32', color: done ? '#32CD32' : '#000', border:`1px solid #32CD32`, padding:'6px 14px', fontFamily:'monospace', fontSize:'10px', fontWeight:700, cursor: done ? 'default' : 'pointer', letterSpacing:'1px', whiteSpace:'nowrap' as const } as React.CSSProperties),
  };

  return (
    <div style={S.panel}>
      <div style={S.header}>AI LOAD MATCHER</div>
      <div style={S.sub}>Scores every available driver against every open load. One click to dispatch.</div>

      <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' as const }}>
        <button onClick={runMatcher} disabled={loading} style={{ ...S.btn(), background:'#32CD32', color:'#000', padding:'10px 20px' }}>
          {loading ? 'ANALYZING...' : '⚡ RUN MATCHER'}
        </button>
        {matches.length > 0 && (
          <>
            <button onClick={() => setView('top')} style={S.btn(view === 'top')}>TOP MATCHES</button>
            <button onClick={() => setView('matrix')} style={S.btn(view === 'matrix')}>FULL MATRIX</button>
          </>
        )}
        {meta && (
          <span style={{ color:'#555', fontSize:'11px', padding:'10px 0' }}>
            {meta.drivers} drivers · {meta.loads} open loads · {meta.busy} busy
          </span>
        )}
      </div>

      {error && <div style={{ color:'#ff4444', fontSize:'12px', marginBottom:'12px' }}>{error}</div>}

      {summary && (
        <div style={{ background:'#0a0a0a', border:'1px solid #32CD32', borderLeft:'3px solid #32CD32', padding:'12px 16px', marginBottom:'16px', color:'#aaa', fontSize:'13px', lineHeight:'1.6' }}>
          <span style={{ color:'#32CD32', fontWeight:700, fontSize:'10px', letterSpacing:'1px' }}>JAKE SAYS  </span>
          {summary}
        </div>
      )}

      {!loading && matches.length === 0 && !error && (
        <div style={{ color:'#333', fontSize:'12px', padding:'20px 0' }}>
          Click RUN MATCHER to score available drivers against open loads.
        </div>
      )}

      {/* TOP MATCHES VIEW */}
      {view === 'top' && matches.map((m, i) => {
        const key = `${m.driver_id}-${m.load_id}`;
        const isDone = assigned[key];
        const isAssigning = assigning === key;
        return (
          <div key={key} style={S.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px' }}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                  <span style={S.score(m.score)}>{m.score}</span>
                  <span style={{ color:'#fff', fontWeight:700, fontSize:'14px' }}>{m.driver_name}</span>
                  <span style={{ color:'#555', fontSize:'11px' }}>{m.driver_city}</span>
                  <span style={{ color:'#555', fontSize:'10px' }}>{m.driver_status}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                  <span style={{ color:'#32CD32', fontSize:'13px', fontWeight:700 }}>→ Load {m.load_number}</span>
                  <span style={{ color:'#aaa', fontSize:'12px' }}>{m.pickup_city}, {m.pickup_state} → {m.delivery_city}, {m.delivery_state}</span>
                  {m.profit_band && <span style={{ color:band(m.profit_band), fontSize:'10px', fontWeight:700 }}>{m.profit_band}</span>}
                </div>
                <div style={{ display:'flex', gap:'12px', marginBottom:'8px' }}>
                  <span style={{ color:'#32CD32', fontWeight:700 }}>${m.rate.toLocaleString()}</span>
                  <span style={{ color:'#555', fontSize:'11px' }}>${m.rpm}/mi</span>
                  <span style={{ color:'#555', fontSize:'11px' }}>{m.miles} mi</span>
                  <span style={{ color:'#555', fontSize:'11px' }}>{m.customer}</span>
                </div>
                <div>
                  {m.flags.map((f: string, fi: number) => (
                    <span key={fi} style={S.tag}>{f}</span>
                  ))}
                </div>
              </div>
              <button
                onClick={() => !isDone && !isAssigning && handleAssign(m.driver_id, m.load_id, m.driver_name, m.load_number)}
                style={S.assignBtn(isDone)}
                disabled={isDone || assigning === key}
              >
                {isDone ? '✓ DISPATCHED' : isAssigning ? 'ASSIGNING...' : 'DISPATCH →'}
              </button>
            </div>
          </div>
        );
      })}

      {/* MATRIX VIEW */}
      {view === 'matrix' && allMatches.length > 0 && (
        <div style={{ overflowX:'auto' as const }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px', fontFamily:'monospace' }}>
            <thead>
              <tr style={{ background:'#0a0a0a' }}>
                {['SCORE','DRIVER','LOCATION','LOAD','ROUTE','RATE','FLAGS','ACTION'].map(h => (
                  <th key={h} style={{ padding:'8px 10px', color:'#555', textAlign:'left', borderBottom:'1px solid #1a1a1a', whiteSpace:'nowrap' as const }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allMatches.map((m, i) => {
                const key = `${m.driver_id}-${m.load_id}`;
                const isDone = assigned[key];
                return (
                  <tr key={key} style={{ borderBottom:'1px solid #0a0a0a', background: i%2 ? '#030303' : '#000' }}>
                    <td style={{ padding:'8px 10px' }}><span style={S.score(m.score)}>{m.score}</span></td>
                    <td style={{ padding:'8px 10px', color:'#fff', fontWeight:700 }}>{m.driver_name}</td>
                    <td style={{ padding:'8px 10px', color:'#555' }}>{m.driver_city}</td>
                    <td style={{ padding:'8px 10px', color:'#32CD32', fontWeight:700 }}>#{m.load_number}</td>
                    <td style={{ padding:'8px 10px', color:'#aaa' }}>{m.pickup_city} → {m.delivery_city}</td>
                    <td style={{ padding:'8px 10px', color:'#32CD32' }}>${m.rate.toLocaleString()}</td>
                    <td style={{ padding:'8px 10px', maxWidth:'200px' }}>
                      {m.flags.slice(0,2).map((f: string, fi: number) => <span key={fi} style={S.tag}>{f}</span>)}
                    </td>
                    <td style={{ padding:'8px 10px' }}>
                      <button onClick={() => !isDone && handleAssign(m.driver_id, m.load_id, m.driver_name, m.load_number)} style={S.assignBtn(isDone)} disabled={isDone}>
                        {isDone ? '✓' : 'GO →'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
